(function() {
  'use strict';
  
  // Constants
  const SELECTORS = {
    video: '#ofen-2-video',
    hotspotsContainer: '#hotspots-container',
    section: '.section-explore-ofen-2',
    hotspot1: '.explore-hotspot-1',
    card1: '.explore-hashboard-card',
    hotspot2: '.explore-hotspot-2',
    card2: '.explore-quiet-fans-card',
    hotspot3: '.explore-hotspot-3',
    card3: '.explore-thermostat-card',
    hotspot4: '.explore-hotspot-4',
    card4: '.explore-materials-card',
    hotspot5: '.explore-hotspot-5',
    card5: '.explore-touch-safe-card'
  };
  
  const CONFIG = {
    intersectionThreshold: 0.3,
    hotspotFadeDelay: 700,
    videoErrorDelay: 1000,
    hotspotZIndex: '1'
  };
  
  // State
  let hasPlayed = false;
  let hoverSetupDone = false;
  let openCardsCount = 0;
  let explodedViewHeight = null;
  let contentHeight = null;
  let sectionHeight = null;
  let maxWidthContainerHeight = null;
  
  // DOM elements
  let video, hotspotsContainer, section, cardsContainer, visualContainer;
  
  /**
   * Position cards container to match visual container
   */
  function positionCardsContainer() {
    if (!cardsContainer || !visualContainer || !section) return;
    
    // Calculate position of visual container relative to section
    // This keeps cards in place when scrolling (they move with the section)
    const visualRect = visualContainer.getBoundingClientRect();
    const sectionRect = section.getBoundingClientRect();
    
    // Use getBoundingClientRect but account for scroll to get document-relative position
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    
    // Calculate absolute position in document
    const visualTop = visualRect.top + scrollTop;
    const visualLeft = visualRect.left + scrollLeft;
    const sectionTop = sectionRect.top + scrollTop;
    const sectionLeft = sectionRect.left + scrollLeft;
    
    // Position relative to section
    cardsContainer.style.top = (visualTop - sectionTop) + 'px';
    cardsContainer.style.left = (visualLeft - sectionLeft) + 'px';
    cardsContainer.style.width = visualRect.width + 'px';
    cardsContainer.style.height = visualRect.height + 'px';
  }
  
  /**
   * Setup resize listener for cards container positioning
   */
  function setupCardsContainerResize() {
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        positionCardsContainer();
      }, 100);
    });
  }
  
  /**
   * Initialize the module
   */
  function init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
      return;
    }
    
    video = document.querySelector(SELECTORS.video);
    hotspotsContainer = document.querySelector(SELECTORS.hotspotsContainer);
    section = document.querySelector(SELECTORS.section);
    cardsContainer = section?.querySelector('.explore-product-cards-container');
    visualContainer = section?.querySelector('.explore-ofen-2-visual');
    
    if (!video || !hotspotsContainer || !section || !cardsContainer || !visualContainer) {
      return;
    }
    
    // Position cards container to match visual container
    positionCardsContainer();
    setupCardsContainerResize();
    
    // Reposition after video loads (in case layout changes)
    video.addEventListener('loadedmetadata', positionCardsContainer, { once: true });
    
    // Hide video initially to prevent poster image flash
    // Show video only once it's ready to play
    const showVideoWhenReady = () => {
      video.classList.add('video-ready');
    };
    
    // Check if video is already ready
    if (video.readyState >= 3) { // HAVE_FUTURE_DATA or higher
      showVideoWhenReady();
    } else {
      // Wait for canplay event (fires when video can start playing)
      video.addEventListener('canplay', showVideoWhenReady, { once: true });
      // Fallback to loadeddata if canplay doesn't fire
      video.addEventListener('loadeddata', showVideoWhenReady, { once: true });
    }
    
    initializeVideoAnimation();
    initializeCardVisibility();
    setupVideoErrorHandling();
  }
  
  /**
   * Setup scroll-triggered video animation
   */
  function initializeVideoAnimation() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !hasPlayed) {
          playAnimation();
        }
      });
    }, {
      threshold: CONFIG.intersectionThreshold
    });
    
    observer.observe(section);
  }
  
  /**
   * Play the video animation
   */
  function playAnimation() {
    if (hasPlayed) return;
    hasPlayed = true;
    
    const playPromise = video.play();
    
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          // Video started playing successfully
        })
        .catch(() => {
          // Fallback: show hotspots immediately if video fails to play
          showHotspots();
        });
    }
    
    // Listen for video end
    video.addEventListener('ended', showHotspots, { once: true });
  }
  
  /**
   * Show hotspots with fade-in animation
   */
  function showHotspots() {
    if (!hotspotsContainer) return;
    
    hotspotsContainer.style.opacity = '0';
    hotspotsContainer.style.display = 'block';
    
    // Get all hotspots
    const hotspots = hotspotsContainer.querySelectorAll('.explore-hotspot');
    
    // Reset hotspots to initial state (remove visible class and ensure hidden)
    hotspots.forEach(hotspot => {
      hotspot.classList.remove('hotspot-visible');
      hotspot.style.opacity = '0';
      hotspot.style.transform = 'scale(0.8)';
    });
    
    requestAnimationFrame(() => {
      // Fade in container first
      hotspotsContainer.style.transition = 'opacity 0.6s ease-in-out';
      hotspotsContainer.style.opacity = '1';
      
      // Wait a frame to ensure container is visible, then fade in all hotspots at once
      requestAnimationFrame(() => {
        setTimeout(() => {
          hotspots.forEach(hotspot => {
            hotspot.classList.add('hotspot-visible');
          });
        }, 50); // Small delay to ensure smooth animation
      });
      
      // Setup hover interactions after animation completes
      setTimeout(() => {
        setupHotspotHover();
      }, CONFIG.hotspotFadeDelay + 50); // Wait for fade-in animation (600ms)
    });
  }
  
  /**
   * Setup hover interactions for hotspots
   */
  function setupHotspotHover() {
    if (hoverSetupDone) return;
    
    // Hotspot 1 - Hashboard
    const hotspot1 = document.querySelector(SELECTORS.hotspot1);
    const card1 = document.querySelector(SELECTORS.card1);
    
    if (hotspot1 && card1) {
      hotspot1.style.zIndex = CONFIG.hotspotZIndex;
      attachHoverHandlers(hotspot1, card1);
    }
    
    // Hotspot 2 - Quiet Fans
    const hotspot2 = document.querySelector(SELECTORS.hotspot2);
    const card2 = document.querySelector(SELECTORS.card2);
    
    if (hotspot2 && card2) {
      hotspot2.style.zIndex = CONFIG.hotspotZIndex;
      attachHoverHandlers(hotspot2, card2);
    }
    
    // Hotspot 3 - Thermostat
    const hotspot3 = document.querySelector(SELECTORS.hotspot3);
    const card3 = document.querySelector(SELECTORS.card3);
    
    if (hotspot3 && card3) {
      hotspot3.style.zIndex = CONFIG.hotspotZIndex;
      attachHoverHandlers(hotspot3, card3);
    }
    
    // Hotspot 4 - Materials
    const hotspot4 = document.querySelector(SELECTORS.hotspot4);
    const card4 = document.querySelector(SELECTORS.card4);
    
    if (hotspot4 && card4) {
      hotspot4.style.zIndex = CONFIG.hotspotZIndex;
      attachHoverHandlers(hotspot4, card4);
    }
    
    // Hotspot 5 - Touch Safe
    const hotspot5 = document.querySelector(SELECTORS.hotspot5);
    const card5 = document.querySelector(SELECTORS.card5);
    
    if (hotspot5 && card5) {
      hotspot5.style.zIndex = CONFIG.hotspotZIndex;
      attachHoverHandlers(hotspot5, card5);
    }
    
    hoverSetupDone = true;
  }
  
  /**
   * Prevent section from scrolling when cards are open
   */
  function preventSectionScroll() {
    if (openCardsCount === 0 && section) {
      section.classList.add('card-open-no-scroll');
      
      // Lock ALL container heights to prevent ANY expansion
      if (!sectionHeight) {
        sectionHeight = section.offsetHeight;
        section.style.height = sectionHeight + 'px';
        section.style.maxHeight = sectionHeight + 'px';
        section.style.overflow = 'visible';
        section.style.minHeight = sectionHeight + 'px';
      }
      
      // Lock max-width container
      const maxWidthContainer = section.querySelector('.max-width-2200');
      if (maxWidthContainer && !maxWidthContainerHeight) {
        maxWidthContainerHeight = maxWidthContainer.offsetHeight;
        maxWidthContainer.style.height = maxWidthContainerHeight + 'px';
        maxWidthContainer.style.maxHeight = maxWidthContainerHeight + 'px';
        maxWidthContainer.style.overflow = 'visible';
      }
      
      // Lock the height of content container to prevent expansion
      const contentContainer = section.querySelector('.explore-ofen-2-content');
      if (contentContainer && !contentHeight) {
        contentHeight = contentContainer.offsetHeight;
        contentContainer.style.height = contentHeight + 'px';
        contentContainer.style.maxHeight = contentHeight + 'px';
        contentContainer.style.overflow = 'visible';
      }
      
      // Lock the height of exploded view to prevent expansion (but allow overflow for cards)
      const explodedView = section.querySelector('.ofen-2-exploded-view');
      if (explodedView && !explodedViewHeight) {
        explodedViewHeight = explodedView.offsetHeight;
        explodedView.style.height = explodedViewHeight + 'px';
        explodedView.style.maxHeight = explodedViewHeight + 'px';
        explodedView.style.overflow = 'visible';
      }
      
      // Reposition cards container
      positionCardsContainer();
    }
    openCardsCount++;
  }
  
  /**
   * Allow section scrolling when all cards are closed
   */
  function allowSectionScroll() {
    openCardsCount--;
    if (openCardsCount <= 0) {
      openCardsCount = 0;
      if (section) {
        section.classList.remove('card-open-no-scroll');
        
        // Restore section height
        if (sectionHeight) {
          section.style.height = '';
          section.style.maxHeight = '';
          section.style.minHeight = '';
          section.style.overflow = '';
          sectionHeight = null;
        }
        
        // Restore max-width container
        const maxWidthContainer = section.querySelector('.max-width-2200');
        if (maxWidthContainer && maxWidthContainerHeight) {
          maxWidthContainer.style.height = '';
          maxWidthContainer.style.maxHeight = '';
          maxWidthContainer.style.overflow = '';
          maxWidthContainerHeight = null;
        }
        
        // Restore content container height
        const contentContainer = section.querySelector('.explore-ofen-2-content');
        if (contentContainer && contentHeight) {
          contentContainer.style.height = '';
          contentContainer.style.maxHeight = '';
          contentContainer.style.overflow = '';
          contentHeight = null;
        }
        
        // Restore exploded view height
        const explodedView = section.querySelector('.ofen-2-exploded-view');
        if (explodedView && explodedViewHeight) {
          explodedView.style.height = '';
          explodedView.style.maxHeight = '';
          explodedView.style.overflow = '';
          explodedViewHeight = null;
        }
        
        // Reposition cards container
        positionCardsContainer();
      }
    }
  }
  
  /**
   * Position card relative to hotspot with fixed gap
   * @param {HTMLElement} hotspot - The hotspot element
   * @param {HTMLElement} card - The product card element
   * @param {boolean} isBelow - Whether card should be below hotspot
   */
  function positionCardRelativeToHotspot(hotspot, card, isBelow) {
    if (!cardsContainer || !hotspot || !card) return;
    
    const GAP = 20; // Fixed gap in pixels between hotspot and card
    const hotspotRect = hotspot.getBoundingClientRect();
    const containerRect = cardsContainer.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    
    // Calculate hotspot position relative to cards container
    const hotspotTop = hotspotRect.top - containerRect.top;
    const hotspotLeft = hotspotRect.left - containerRect.left;
    const hotspotHeight = hotspotRect.height;
    
    // Get card dimensions using getBoundingClientRect for accuracy
    const cardWidth = cardRect.width || card.offsetWidth || 248;
    const cardHeight = cardRect.height || card.offsetHeight || 310;
    
    // Calculate card position with fixed gap
    let cardTop;
    if (isBelow) {
      // Position card below hotspot with fixed gap
      cardTop = hotspotTop + hotspotHeight + GAP;
    } else {
      // Position card above hotspot with fixed gap
      cardTop = hotspotTop - cardHeight - GAP;
    }
    
    // Center card horizontally relative to hotspot
    const cardLeft = hotspotLeft + (hotspotRect.width / 2) - (cardWidth / 2);
    
    // Apply position via inline styles with !important to override any CSS
    card.style.setProperty('top', cardTop + 'px', 'important');
    card.style.setProperty('left', cardLeft + 'px', 'important');
  }
  
  /**
   * Attach hover event handlers to hotspot and card
   * @param {HTMLElement} hotspot - The hotspot element
   * @param {HTMLElement} card - The product card element
   */
  function attachHoverHandlers(hotspot, card) {
    let isCardOpen = false;
    
    const showCard = () => {
      if (isCardOpen) return;
      isCardOpen = true;
      
      // Stop hotspot animation immediately when card opens
      hotspot.classList.add('card-open');
      hotspot.style.transform = 'scale(1)';
      
      // Prevent section scrolling
      preventSectionScroll();
      
      // Check if hotspot is in top 50% of viewport
      const hotspotRect = hotspot.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const hotspotCenterY = hotspotRect.top + (hotspotRect.height / 2);
      const isInTopHalf = hotspotCenterY < (viewportHeight / 2);
      
      // Position card based on hotspot location
      let isBelow;
      if (isInTopHalf) {
        // Hotspot in top half - show card below
        card.classList.add('show', 'card-below');
        card.classList.remove('card-above');
        isBelow = true;
      } else {
        // Hotspot in bottom half - show card above (default)
        card.classList.add('show', 'card-above');
        card.classList.remove('card-below');
        isBelow = false;
      }
      
      // Make card visible first to get accurate measurements
      card.style.opacity = '1';
      card.style.visibility = 'visible';
      card.style.pointerEvents = 'auto';
      
      // Use double requestAnimationFrame to ensure card is fully rendered before measuring
      // This is especially important for cards positioned below hotspots
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Position card dynamically with fixed gap from hotspot
          positionCardRelativeToHotspot(hotspot, card, isBelow);
        });
      });
    };
    
    const hideCard = () => {
      if (!isCardOpen) return;
      isCardOpen = false;
      
      // Remove card-open class to restore normal hotspot animation
      hotspot.classList.remove('card-open');
      hotspot.style.transform = '';
      
      // Only remove 'show' class, keep positioning classes (card-above/card-below)
      // so the card stays in the same position during fade-out
      card.classList.remove('show');
      card.style.opacity = '0';
      card.style.visibility = 'hidden';
      card.style.pointerEvents = 'none';
      
      // Delay allowing section scrolling until after the fade-out animation completes
      // This ensures the card can animate out without being clipped by parent overflow
      // Transition duration is 0.3s, so wait 350ms to be safe
      setTimeout(() => {
        allowSectionScroll();
      }, 350);
    };
    
    hotspot.addEventListener('mouseenter', showCard);
    hotspot.addEventListener('mouseleave', hideCard);
    card.addEventListener('mouseenter', showCard);
    card.addEventListener('mouseleave', hideCard);
  }
  
  /**
   * Ensure product cards are initially hidden
   */
  function initializeCardVisibility() {
    const card1 = document.querySelector(SELECTORS.card1);
    const card2 = document.querySelector(SELECTORS.card2);
    const card3 = document.querySelector(SELECTORS.card3);
    const card4 = document.querySelector(SELECTORS.card4);
    const card5 = document.querySelector(SELECTORS.card5);
    
    if (card1) {
      hideCard(card1);
    }
    
    if (card2) {
      hideCard(card2);
    }
    
    if (card3) {
      hideCard(card3);
    }
    
    if (card4) {
      hideCard(card4);
    }
    
    if (card5) {
      hideCard(card5);
    }
  }
  
  /**
   * Hide a card element
   * @param {HTMLElement} card - The card element to hide
   */
  function hideCard(card) {
    // Only remove 'show' class, keep positioning classes during hide
    card.classList.remove('show');
    card.style.opacity = '0';
    card.style.visibility = 'hidden';
    card.style.pointerEvents = 'none';
  }
  
  /**
   * Setup error handling for video
   */
  function setupVideoErrorHandling() {
    if (!video) return;
    
    video.addEventListener('error', () => {
      setTimeout(showHotspots, CONFIG.videoErrorDelay);
    }, { once: true });
  }
  
  // Initialize when DOM is ready
  init();
})();

